import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const OurStoryPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    interests: [] as string[],
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleCheckbox = (val: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(val)
        ? prev.interests.filter(i => i !== val)
        : [...prev.interests, val],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    if (formData.message.length > 500) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('phone', formData.phone);
      body.append('email', formData.email);
      body.append('interests', formData.interests.join(', '));
      body.append('message', formData.message);
      await fetch('https://readdy.ai/api/form/d7chn2k5q56ra7tuf770', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const trio = [
    {
      img: 'https://i.ibb.co/1tDS4F9y/Leda-Nova-Foto.jpg',
      alt: 'Leda',
      name: 'Leda',
      roleKey: 'story_leda_role',
      bioKey: 'story_leda_bio',
    },
    {
      img: 'https://i.ibb.co/JRZXfbMp/Nova-foto-Thomas.jpg',
      alt: 'Thomas',
      name: 'Thomas',
      roleKey: 'story_thomas_role',
      bioKey: 'story_thomas_bio',
    },
    {
      img: 'https://i.ibb.co/RWrpFfG/8b4385bc-e9d6-47a1-a159-ae0ca54be535.jpg',
      alt: 'Jade',
      name: 'Jade',
      roleKey: 'story_jade_role',
      bioKey: 'story_jade_bio',
    },
  ];

  return (
    <>
      <Navbar />
      <main className="bg-[#0d0d0d] min-h-screen" style={{ fontFamily: "'Cormorant Garamond', serif" }}>

        {/* Hero Banner */}
        <section className="relative h-[70vh] min-h-[500px] flex flex-col items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://magadenidestilados.com/wp-content/uploads/2025/11/Uisque-ao-Por-do-Sol.png')" }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#0d0d0d]" />
          <div className="relative z-10 text-center px-6 w-full">
            <p className="text-amber-400/80 text-[10px] tracking-[0.4em] uppercase mb-4 font-light" style={{ fontFamily: 'sans-serif' }}>
              {t('story_hero_tagline')}
            </p>
            <h1 className="text-5xl md:text-7xl font-light text-white tracking-wide">
              {t('story_hero_title')}
            </h1>
            <div className="mt-5 flex items-center justify-center gap-2 text-white/40 text-xs" style={{ fontFamily: 'sans-serif' }}>
              <Link to="/" className="hover:text-amber-400 transition-colors">{t('story_breadcrumb_home')}</Link>
              <span>/</span>
              <span className="text-white/60">{t('story_hero_title')}</span>
            </div>
            <p className="mt-6 text-white/60 text-sm md:text-base font-light max-w-2xl mx-auto leading-relaxed">
              {t('story_hero_desc')}
            </p>
          </div>
        </section>

        {/* Essence + Promise */}
        <section className="py-24 px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-light text-white mb-8 leading-snug">{t('story_essence_title')}</h3>
              <p className="text-white/55 text-base leading-relaxed mb-6 font-light">{t('story_essence_p1')}</p>
              <p className="text-white/55 text-base leading-relaxed font-light">{t('story_essence_p2')}</p>
            </div>
            <div className="bg-[#111] border border-white/8 rounded-lg p-12 flex flex-col justify-center">
              <p className="text-amber-400/70 text-[10px] tracking-[0.35em] uppercase mb-6 font-light" style={{ fontFamily: 'sans-serif' }}>
                {t('story_promise_label')}
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white leading-tight">
                {t('story_promise_quote')}
              </h2>
            </div>
          </div>
        </section>

        {/* Trio */}
        <section className="py-24 px-6 bg-[#080808]">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-light text-white mb-4">{t('story_trio_title')}</h2>
              <p className="text-white/45 text-base font-light">{t('story_trio_subtitle')}</p>
              <div className="w-16 h-px bg-amber-700/50 mx-auto mt-6" />
              <p className="text-white/50 text-base font-light mt-6 max-w-2xl mx-auto leading-relaxed">{t('story_trio_desc')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {trio.map(person => (
                <div key={person.name} className="group flex flex-col">
                  <div className="relative w-full aspect-[3/4] overflow-hidden rounded-xl mb-6">
                    <img src={person.img} alt={person.alt} className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                    <div className="absolute inset-0 bg-[#c9a84c]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute inset-0 rounded-xl ring-1 ring-[#c9a84c]/0 group-hover:ring-[#c9a84c]/40 transition-all duration-500" />
                    <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-1 group-hover:translate-y-0 transition-transform duration-400">
                      <h3 className="text-xl font-light text-white leading-none mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{person.name}</h3>
                      <p className="text-[#c9a84c]/80 text-[10px] tracking-[0.25em] uppercase font-light" style={{ fontFamily: 'sans-serif' }}>{t(person.roleKey)}</p>
                      <div className="mt-2.5 h-px bg-[#c9a84c]/50 w-0 group-hover:w-full transition-all duration-500 ease-out" />
                    </div>
                  </div>
                  <p className="text-white/45 text-sm leading-relaxed font-light group-hover:text-white/65 transition-colors duration-300">{t(person.bioKey)}</p>
                </div>
              ))}
            </div>

            <p className="text-center text-white/50 text-base font-light max-w-3xl mx-auto leading-relaxed">{t('story_closing')}</p>
          </div>
        </section>

        {/* Explore CTA */}
        <section className="py-20 px-6 text-center">
          <div className="max-w-2xl mx-auto">
            <p className="text-amber-400/70 text-[10px] tracking-[0.4em] uppercase mb-6 font-light" style={{ fontFamily: 'sans-serif' }}>{t('story_explore_label')}</p>
            <p className="text-white/55 text-base font-light leading-relaxed mb-10">{t('story_explore_desc')}</p>
            <Link to="/contact" className="inline-block border border-amber-700/60 text-amber-400/80 hover:bg-amber-700/20 text-xs tracking-[0.25em] uppercase px-12 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer" style={{ fontFamily: 'sans-serif' }}>
              {t('story_contact_us')}
            </Link>
          </div>
        </section>

        {/* Three Pillars */}
        <section className="py-24 px-6 bg-[#080808]">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-white/8">
            <div className="px-8 py-10 md:py-0">
              <p className="text-amber-400/70 text-[10px] tracking-[0.35em] uppercase mb-5 font-light" style={{ fontFamily: 'sans-serif' }}>{t('story_pillar1_label')}</p>
              <p className="text-white/50 text-sm leading-relaxed font-light">{t('story_pillar1_desc')}</p>
            </div>
            <div className="px-8 py-10 md:py-0">
              <p className="text-amber-400/70 text-[10px] tracking-[0.35em] uppercase mb-5 font-light" style={{ fontFamily: 'sans-serif' }}>{t('story_pillar2_label')}</p>
              <p className="text-white/50 text-sm leading-relaxed font-light">{t('story_pillar2_desc')}</p>
            </div>
            <div className="px-8 py-10 md:py-0">
              <p className="text-amber-400/70 text-[10px] tracking-[0.35em] uppercase mb-5 font-light" style={{ fontFamily: 'sans-serif' }}>{t('story_pillar3_label')}</p>
              <p className="text-white/50 text-sm leading-relaxed font-light">{t('story_pillar3_desc')}</p>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-28 px-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-[#0a0a0a]" />
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, #c9a84c 0px, #c9a84c 1px, transparent 1px, transparent 60px), repeating-linear-gradient(90deg, #c9a84c 0px, #c9a84c 1px, transparent 1px, transparent 60px)' }} />
          <div className="relative z-10 max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-[#c9a84c]/60 text-[10px] tracking-[0.45em] uppercase mb-4 font-light" style={{ fontFamily: 'sans-serif' }}>{t('story_form_label')}</p>
              <h2 className="text-4xl md:text-5xl font-light text-white mb-4">{t('story_form_title')}</h2>
              <div className="w-12 h-px bg-[#c9a84c]/40 mx-auto" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
              <div className="lg:col-span-2 flex flex-col gap-10">
                <p className="text-white/45 text-sm leading-relaxed font-light" style={{ fontFamily: 'sans-serif' }}>{t('story_form_desc')}</p>
                <div className="space-y-6">
                  {[
                    { icon: 'ri-map-pin-line', label: t('story_form_location'), value: t('story_form_location_val') },
                    { icon: 'ri-ship-line', label: t('story_form_provenance'), value: t('story_form_provenance_val') },
                    { icon: 'ri-time-line', label: t('story_form_hours'), value: t('story_form_hours_val') },
                  ].map(item => (
                    <div key={item.label} className="flex items-start gap-4">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full border border-[#c9a84c]/25 flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-[#c9a84c]/70 text-sm`}></i>
                      </div>
                      <div style={{ fontFamily: 'sans-serif' }}>
                        <p className="text-white/35 text-[10px] tracking-widest uppercase mb-0.5">{item.label}</p>
                        <p className="text-white/70 text-sm">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-l-2 border-[#c9a84c]/30 pl-5 mt-2">
                  <p className="text-white/30 text-sm font-light italic leading-relaxed">{t('story_form_quote')}</p>
                </div>
              </div>

              <div className="lg:col-span-3">
                {submitted ? (
                  <div className="border border-[#c9a84c]/30 rounded-xl p-14 text-center bg-[#c9a84c]/5">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full border border-[#c9a84c]/40 mx-auto mb-6">
                      <i className="ri-check-line text-2xl text-[#c9a84c]"></i>
                    </div>
                    <p className="text-white text-xl font-light mb-2">{t('story_form_success')}</p>
                    <p className="text-white/35 text-sm font-light" style={{ fontFamily: 'sans-serif' }}>{t('story_form_success_sub')}</p>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleSubmit} className="bg-[#111]/80 border border-white/8 rounded-xl p-8 space-y-6" style={{ fontFamily: 'sans-serif' }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-white/40 text-[10px] tracking-[0.2em] uppercase">{t('story_form_name')} <span className="text-[#c9a84c]">*</span></label>
                        <input name="name" type="text" required value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} className="bg-white/4 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/15 focus:outline-none focus:border-[#c9a84c]/50 transition-all" />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-white/40 text-[10px] tracking-[0.2em] uppercase">{t('story_form_phone')}</label>
                        <input name="phone" type="tel" value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} className="bg-white/4 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/15 focus:outline-none focus:border-[#c9a84c]/50 transition-all" />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-white/40 text-[10px] tracking-[0.2em] uppercase">{t('story_form_email')} <span className="text-[#c9a84c]">*</span></label>
                      <input name="email" type="email" required value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} className="bg-white/4 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/15 focus:outline-none focus:border-[#c9a84c]/50 transition-all" />
                    </div>
                    <div className="flex flex-col gap-2.5">
                      <label className="text-white/40 text-[10px] tracking-[0.2em] uppercase">{t('story_form_interest')}</label>
                      <div className="flex gap-4 flex-wrap">
                        {['Whiskey', 'Gin'].map(opt => (
                          <label key={opt} className="flex items-center gap-2.5 cursor-pointer group">
                            <div onClick={() => handleCheckbox(opt)} className={`w-5 h-5 rounded border flex items-center justify-center transition-all cursor-pointer flex-shrink-0 ${formData.interests.includes(opt) ? 'bg-[#c9a84c] border-[#c9a84c]' : 'border-white/20 bg-transparent group-hover:border-[#c9a84c]/50'}`}>
                              {formData.interests.includes(opt) && <i className="ri-check-line text-black text-xs"></i>}
                            </div>
                            <span className="text-white/55 text-sm group-hover:text-white/80 transition-colors">{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-white/40 text-[10px] tracking-[0.2em] uppercase">{t('story_form_message')} <span className="text-[#c9a84c]">*</span></label>
                      <textarea name="message" required rows={5} maxLength={500} value={formData.message} onChange={e => setFormData(p => ({ ...p, message: e.target.value }))} className="bg-white/4 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/15 focus:outline-none focus:border-[#c9a84c]/50 transition-all resize-none" />
                      <p className={`text-[10px] text-right ${formData.message.length > 500 ? 'text-red-400' : 'text-white/20'}`}>{formData.message.length}/500</p>
                    </div>
                    <button type="submit" disabled={submitting || formData.message.length > 500} className="w-full relative overflow-hidden group disabled:opacity-40 cursor-pointer whitespace-nowrap rounded-lg py-4 transition-all duration-300" style={{ background: 'linear-gradient(135deg, #c9a84c 0%, #a8863a 50%, #c9a84c 100%)', backgroundSize: '200% 100%' }}>
                      <span className="relative z-10 flex items-center justify-center gap-2 text-[#1a0f00] text-xs tracking-[0.3em] uppercase font-semibold">
                        {submitting ? <><i className="ri-loader-4-line animate-spin text-sm"></i>{t('story_form_sending')}</> : <>{t('story_form_send')}<i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform"></i></>}
                      </span>
                      <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
};

export default OurStoryPage;
